/*----------------------------------
- AGENDA CONVERSION SECTION
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import CtaPill from '@uniquedomains/components/website/WebsiteCta';

import { AGENDA_PAGE_CONTENT } from '@/client/catalogs/landing/agenda/pageContent';
import PersonaAuthCtaPair from '@/client/pages/landing/components/conversion/PersonaAuthCtaPair';
import {
    CARD_BODY_CLASS,
    CARD_TITLE_CLASS,
    EYEBROW_CLASS,
    WHITE_CARD_CLASS,
} from '@/client/pages/landing/components/shared/pageStyles';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import { buildWebsiteAuthHref } from '@/client/utils/conversion/authHref';
import { PRODUCT_PAGE_ROUTES } from '@/common/catalogs/appRoutesCatalog';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** Signup attribution for every CTA of the page (`User.desiredFrom`). */
const AGENDA_ATTRIBUTION = 'website_agenda';
const TRACKING_PAGE = 'agenda';

/** The card's in-page anchor, `/agenda#your-dates`: a stable target for a shared link. */
const AGENDA_YOUR_DATES_ANCHOR_ID = 'your-dates';

/**
 * "Get my dates" asks for an account and returns to Product's calendar once it exists: the
 * member's own dates (renewals, watchlist expiries, Radar dates) live behind sign-in.
 */
const MY_DATES_HREF = buildWebsiteAuthHref({
    flow: 'cold',
    mode: 'signup',
    from: AGENDA_ATTRIBUTION,
    returnTo: PRODUCT_PAGE_ROUTES.calendar,
});

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * The page's way into Product, under the newsletter band: the member calendar ("Get my dates"),
 * and the persona pair beside it for a visitor who came for launches or names rather than dates.
 */
export default function AgendaConversionSection() {
    const { myDates } = AGENDA_PAGE_CONTENT;

    return (
        <section
            id={AGENDA_YOUR_DATES_ANCHOR_ID}
            class={`${WHITE_CARD_CLASS} flex scroll-mt-24 flex-col gap-3 p-5 lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:p-8`}
            aria-labelledby="agenda-your-dates-title"
            data-testid="agenda-conversion"
        >
            <div class="flex max-w-[620px] flex-col gap-2">
                <p class={EYEBROW_CLASS}>{myDates.eyebrow}</p>
                <h2 id="agenda-your-dates-title" class={CARD_TITLE_CLASS}>{myDates.heading}</h2>
                <p class={CARD_BODY_CLASS}>{myDates.body}</p>
            </div>
            <div class="mt-1 flex flex-wrap gap-3 lg:mt-0 lg:flex-none">
                <CtaPill
                    href={MY_DATES_HREF}
                    variant="dark"
                    size="md"
                    data-testid="agenda-my-dates-cta"
                    data-attribution={AGENDA_ATTRIBUTION}
                    onClick={() => {
                        trackLandingCta({
                            page: TRACKING_PAGE,
                            section: 'agenda_my_dates',
                            label: myDates.ctaLabel,
                            kind: 'my_dates',
                            href: MY_DATES_HREF,
                            surface: 'agenda',
                        });
                        trackConversionTrigger('website_agenda_my_dates', {
                            href: MY_DATES_HREF,
                            page: TRACKING_PAGE,
                            section: 'agenda_my_dates',
                        });
                    }}
                >
                    {myDates.ctaLabel}
                </CtaPill>
                <PersonaAuthCtaPair
                    attribution={AGENDA_ATTRIBUTION}
                    investorTriggerId="website_agenda_investor"
                    founderTriggerId="website_agenda_founder"
                    page={TRACKING_PAGE}
                    section="agenda_personas"
                    size="md"
                    surface="agenda"
                    tone="light"
                />
            </div>
        </section>
    );
}
